import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth, API } from "@/App";
import { useLanguage } from "@/contexts/LanguageContext";
import { Check, Sparkles, Loader2, Crown, X } from "lucide-react";
import { toast } from "sonner";

export const Paywall = ({ 
  isOpen, 
  onClose, 
  trigger = "general", // "meal_limit", "lana_limit", "insights"
  usageData = null 
}) => {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("monthly"); // "monthly" or "yearly"

  const handleStartTrial = async () => {
    setLoading(true);
    
    try {
      const planId = selectedPlan === "yearly" ? "premium_yearly" : "premium_monthly";
      const response = await fetch(`${API}/subscription/checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        
        body: JSON.stringify({
          origin_url: window.location.origin,
          plan_id: planId
        }),
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      } else {
        toast.error("Failed to start checkout");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Failed to start checkout");
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    t('subscription.benefit_1'),
    t('subscription.benefit_2'),
    t('subscription.benefit_3'),
    t('subscription.benefit_4'),
    t('subscription.benefit_5'),
  ];

  // Get contextual message based on trigger
  const getContextMessage = () => {
    if (trigger === "meal_limit" && usageData) {
      return t('subscription.meal_limit_reached').replace('{total}', usageData.limit);
    }
    if (trigger === "lana_limit" && usageData) {
      return t('subscription.lana_limit_reached');
    }
    if (trigger === "insights") {
      return t('subscription.insights_preview_desc');
    }
    return null;
  };

  const getContextAction = () => {
    if (trigger === "meal_limit") {
      return t('subscription.unlock_meals');
    }
    if (trigger === "lana_limit") {
      return t('subscription.unlock_lana');
    }
    return null;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="rounded-3xl max-w-md p-0 overflow-hidden [&>button]:hidden max-h-[95vh] overflow-y-auto scrollbar-hide [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none]">
        {/* Header */}
        <div className="bg-gradient-to-br from-primary to-primary/80 p-6 text-white relative sticky top-0 z-10">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
            data-testid="paywall-close-btn"
          >
            <X className="w-5 h-5 text-white" />
          </button>
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-6 h-6" />
            <span className="font-bold text-lg">{t('subscription.premium_badge')}</span>
          </div>
          <h2 className="font-heading text-xl font-bold leading-tight">
            {t('subscription.paywall_title')}
          </h2>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Context Message */}
          {getContextMessage() && (
            <div className="bg-orange-50 rounded-2xl p-4 text-center">
              <p className="text-secondary font-medium">{getContextMessage()}</p>
              {getContextAction() && (
                <p className="text-sm text-muted-foreground mt-1">{getContextAction()}</p>
              )}
            </div>
          )}

          {/* Benefits */}
          <div className="space-y-3">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-success/10 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-success" />
                </div>
                <span className="text-secondary">{benefit}</span>
              </div>
            ))}
          </div>

          {/* Plan Selection */}
          <div className="grid grid-cols-2 gap-3">
            {/* Monthly Plan */}
            <button
              onClick={() => setSelectedPlan("monthly")}
              className={`relative p-4 rounded-2xl border-2 transition-all text-left ${
                selectedPlan === "monthly"
                  ? "border-primary bg-primary/5"
                  : "border-gray-200 hover:border-gray-300"
              }`}
              data-testid="plan-monthly"
            >
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">{t('subscription.monthly')}</p>
                <p className="text-2xl font-bold text-secondary">$5</p>
                <p className="text-xs text-muted-foreground">{t('subscription.per_month')}</p>
              </div>
              {selectedPlan === "monthly" && (
                <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
            </button>

            {/* Yearly Plan */}
            <button
              onClick={() => setSelectedPlan("yearly")}
              className={`relative p-4 rounded-2xl border-2 transition-all text-left ${
                selectedPlan === "yearly"
                  ? "border-primary bg-primary/5"
                  : "border-gray-200 hover:border-gray-300"
              }`}
              data-testid="plan-yearly"
            >
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-success text-white text-xs font-bold rounded-full whitespace-nowrap">
                {t('subscription.save_percent')}
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">{t('subscription.yearly')}</p>
                <p className="text-2xl font-bold text-secondary">$50</p>
                <p className="text-xs text-muted-foreground">{t('subscription.per_year')}</p>
              </div>
              {selectedPlan === "yearly" && (
                <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
            </button>
          </div>

          {/* Trial Info */}
          <Card className="bg-gradient-to-br from-orange-50 to-orange-100/50 border-none rounded-2xl p-4 text-center space-y-1">
            <div className="flex items-center justify-center gap-2">
              <Crown className="w-5 h-5 text-primary" />
              <p className="font-bold text-primary">{t('subscription.free_trial')}</p>
            </div>
            <p className="text-sm text-muted-foreground">
              {selectedPlan === "yearly" 
                ? t('subscription.then_yearly')
                : t('subscription.then_monthly')
              }
            </p>
          </Card>

          {/* CTA Buttons */}
          <div className="space-y-3">
            <Button
              onClick={handleStartTrial}
              disabled={loading}
              className="w-full rounded-full bg-primary text-white hover:bg-primary/90 py-6 font-bold shadow-lg"
              data-testid="start-trial-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {t('common.loading')}
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  {t('subscription.start_free_trial')}
                </>
              )}
            </Button>
            <Button
              variant="ghost"
              onClick={onClose}
              className="w-full rounded-full text-muted-foreground hover:text-secondary py-4"
              data-testid="continue-free-btn"
            >
              {trigger === "insights" ? t('subscription.maybe_later') : t('subscription.continue_free')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Usage Progress Component
export const UsageProgress = ({ type, used, limit, isPremium }) => {
  const { t } = useLanguage();
  
  const percentage = limit > 0 ? (used / limit) * 100 : 0;
  const isAtLimit = used >= limit;
  
  const getMessage = () => {
    if (type === "meal") {
      if (isAtLimit) {
        return t('subscription.meal_limit_reached').replace('{total}', limit);
      }
      return t('subscription.meal_usage').replace('{current}', used).replace('{total}', limit);
    }
    if (type === "lana") {
      if (isAtLimit) {
        return t('subscription.lana_limit_reached');
      }
      return t('subscription.lana_usage').replace('{current}', used).replace('{total}', limit);
    }
    return "";
  };

  return (
    <div className={`rounded-xl p-3 space-y-2 ${isPremium ? "bg-blue-50" : "bg-orange-50"}`}>
      <div className="flex items-center justify-between text-sm">
        <span className={isAtLimit ? "text-warning font-medium" : "text-muted-foreground"}>
          {getMessage()}
        </span>
        {!isAtLimit && (
          <span className="text-xs text-muted-foreground">
            {t('subscription.remaining').replace('{count}', limit - used)}
          </span>
        )}
      </div>
      <div className="h-2 bg-white rounded-full overflow-hidden">
        <div 
          className={`h-full rounded-full transition-all ${
            isAtLimit ? "bg-warning" : isPremium ? "bg-blue-500" : percentage > 66 ? "bg-warning" : "bg-primary"
          }`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
    </div>
  );
};

export default Paywall;
